/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import queryString from 'query-string';
import { FormattedMessage as FM } from 'react-intl';
import { PageContentPreloader, DateCell, MoneyCell, Tooltip, NoItemsPanel } from 'common/components';
import { Pagination } from 'crm/_components';
import noPaymentsImage from 'common/images/noPayments.svg';
import ClosedSubRowIcon from 'crm/_newComponents/UpdatedCRMTable/icons/ClosedSubRow.svg';
import OpenedSubRowIcon from 'crm/_newComponents/UpdatedCRMTable/icons/OpenedSubRow.svg';

import SelectCheckbox from '../SelectCheckbox';
import NewFullNameCell from '../NewContactsTable/NewFullNameCell';
import { getContactsIBTree, crmSetSelectedContactsIds } from '../../_redux';
import { staticStyles } from './style';

// ─── 工具：递归收集节点下所有 contact id ──────────────────────────────────────

const collectAllIds = node => {
  const ids = [node.id];
  // 确保 children 是数组
  if (Array.isArray(node.children)) {
    node.children.forEach(child => {
      if (child.node_type === 'ib') {
        ids.push(...collectAllIds(child));
      } else {
        ids.push(child.id);
      }
    });
  }
  return ids;
};

const renderManagerNames = (managerName, id) => {
  const managers = managerName.split(';');
  const displayText = managers.length > 2 ? `${managers[0]};${managers[1]};...` : managerName;
  if (managers.length > 2) {
    return (
      <Tooltip id={`manager-names-${id}`} textId={managerName} noTranslate>
        <span>{displayText}</span>
      </Tooltip>
    );
  }
  return <span>{displayText}</span>;
};

// ─── IB 汇总行 ───────────────────────────────────────────────────────────────

const IBSummaryRow = ({ node, depth, onTagSelect, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const indentPx = depth * 24;
  const dispatch = useDispatch();
  const selectedContactsIds = useSelector(state => state.crm.contacts.selectedContactsIds);

  const subtreeIds = useMemo(() => collectAllIds(node), [node]);
  const isAllSelected = useMemo(
    () => subtreeIds.length > 0 && subtreeIds.every(id => selectedContactsIds.includes(id)),
    [subtreeIds, selectedContactsIds]
  );

  const handleIBCheckboxChange = () => {
    if (isAllSelected) {
      dispatch(crmSetSelectedContactsIds(selectedContactsIds.filter(id => !subtreeIds.includes(id))));
    } else {
      const merged = Array.from(new Set([...selectedContactsIds, ...subtreeIds]));
      dispatch(crmSetSelectedContactsIds(merged));
    }
  };

  const children = Array.isArray(node.children) ? node.children : [];
  // children[0] 是 IB 本人数据行的条件：node_type === 'user' 且 id 与当前 IB 节点相同
  const firstChild = children[0] || null;
  const selfRow = firstChild && firstChild.node_type === 'user' && firstChild.id === node.id ? firstChild : null;
  const subChildren = selfRow ? children.slice(1) : children;
  // 有 selfRow 或有下属，都可以展开
  const hasSubChildren = !!selfRow || subChildren.length > 0;

  return (
    <>
      {/* IB 汇总行 */}
      <div className="IBTreeTable__Row IBTreeTable__Row--ib" style={{ paddingLeft: `${indentPx}px` }}>
        {/* checkbox — 选中时联动整棵子树 */}
        <div className="IBTreeTable__Cell IBTreeTable__Cell--checkbox" onClick={e => e.stopPropagation()}>
          <div className="checkboxWrapper">
            <input type="checkbox" className="CheckBox" checked={isAllSelected} onChange={handleIBCheckboxChange} />
          </div>
        </div>
        {/* expand + name */}
        <div
          className="IBTreeTable__Cell IBTreeTable__Cell--name IBTreeTable__Cell--ib-name"
          onClick={() => hasSubChildren && setIsOpen(prev => !prev)}>
          {hasSubChildren ? (
            <img src={isOpen ? OpenedSubRowIcon : ClosedSubRowIcon} className="IBTreeTable__ExpandIcon" alt="expand" />
          ) : (
            <span className="IBTreeTable__ExpandIcon--placeholder" />
          )}
          <div className="IBTreeTable__IBNameWrapper">
            <span className="IBTreeTable__IBLabel">IB level{node.ib_level}</span>
            <NewFullNameCell id={node.id} fullName={node.full_name} isManager={node.is_manager} isNew={node.is_new} />
          </div>
        </div>
        {/* last seen */}
        <div className="IBTreeTable__Cell">--</div>
        {/* manager name */}
        <div className="IBTreeTable__Cell">
          {node.manager_name ? renderManagerNames(node.manager_name, node.id) : '--'}
        </div>
        {/* total in */}
        <div className="IBTreeTable__Cell">
          <MoneyCell crmStyle heightMaxContent value={node.total_in} />
        </div>
        {/* total out */}
        <div className="IBTreeTable__Cell">
          <MoneyCell crmStyle heightMaxContent value={node.total_out} />
        </div>
        {/* in-out */}
        <div className="IBTreeTable__Cell">
          <MoneyCell crmStyle heightMaxContent value={node.total_diff} />
        </div>
        {/* trading account */}
        <div className="IBTreeTable__Cell">--</div>
        {/* balance */}
        <div className="IBTreeTable__Cell">
          <MoneyCell crmStyle heightMaxContent value={node.balance} />
        </div>
        {/* total lots */}
        <div className="IBTreeTable__Cell">
          {node.total_lots !== null && node.total_lots !== undefined ? node.total_lots : '--'}
        </div>
        {/* last comment */}
        <div className="IBTreeTable__Cell">--</div>
      </div>
      {/* 展开后显示：IB 本人数据行 + 下属（子 IB / 普通用户），使用相同缩进 */}
      {isOpen && (
        <div className="IBTreeTable__Children">
          {selfRow && <UserRow rowData={selfRow} depth={depth + 1} onTagSelect={onTagSelect} />}
          {subChildren.map((child, idx) =>
            child.node_type === 'ib' ? (
              <IBSummaryRow
                key={`ib-${child.id}-${idx}`}
                node={child}
                depth={depth + 1}
                onTagSelect={onTagSelect}
                defaultOpen={defaultOpen}
              />
            ) : (
              <UserRow key={`user-${child.id}-${idx}`} rowData={child} depth={depth + 1} onTagSelect={onTagSelect} />
            )
          )}
        </div>
      )}
    </>
  );
};

// ─── 普通用户行 ───────────────────────────────────────────────────────────────

const renderMt5Accounts = (mt5Accounts, id) => {
  const accounts = mt5Accounts.split(';');
  const displayText = accounts.length > 2 ? `${accounts[0]};${accounts[1]};...` : mt5Accounts;
  if (accounts.length > 2) {
    return (
      <Tooltip id={`mt5-accounts-${id}`} textId={mt5Accounts} noTranslate>
        <span>{displayText}</span>
      </Tooltip>
    );
  }
  return <span>{displayText}</span>;
};

const UserRow = ({ rowData, depth }) => {
  const indentPx = depth * 24;

  return (
    <div className="IBTreeTable__Row IBTreeTable__Row--user" style={{ paddingLeft: `${indentPx}px` }}>
      {/* checkbox */}
      <div className="IBTreeTable__Cell IBTreeTable__Cell--checkbox">
        <SelectCheckbox contactId={rowData.id} />
      </div>
      {/* name */}
      <div className="IBTreeTable__Cell IBTreeTable__Cell--name">
        <NewFullNameCell
          id={rowData.id}
          fullName={rowData.fullName}
          isManager={rowData.isManager}
          isNew={rowData.isNew}
        />
      </div>
      {/* last seen */}
      <div className="IBTreeTable__Cell">
        {rowData.lastSeen ? <DateCell value={rowData.lastSeen} hideTime grey /> : '---'}
      </div>
      {/* manager name */}
      <div className="IBTreeTable__Cell">{rowData.managerName || '---'}</div>
      {/* total in */}
      <div className="IBTreeTable__Cell">
        <MoneyCell crmStyle heightMaxContent value={rowData.totalIn} />
      </div>
      {/* total out */}
      <div className="IBTreeTable__Cell">
        <MoneyCell crmStyle heightMaxContent value={rowData.totalOut} />
      </div>
      {/* in-out */}
      <div className="IBTreeTable__Cell">
        <MoneyCell crmStyle heightMaxContent value={rowData.totalDiff} />
      </div>
      {/* trading account */}
      <div className="IBTreeTable__Cell">
        {rowData.mt5Accounts ? renderMt5Accounts(rowData.mt5Accounts, rowData.id) : '---'}
      </div>
      {/* balance */}
      <div className="IBTreeTable__Cell">
        <MoneyCell crmStyle heightMaxContent value={rowData.balance} />
      </div>
      {/* total lots */}
      <div className="IBTreeTable__Cell">
        {rowData.totalLots !== null && rowData.totalLots !== undefined ? rowData.totalLots : '---'}
      </div>
      {/* last comment */}
      <div className="IBTreeTable__Cell">{rowData.lastTask?.comment ? rowData.lastTask.comment : '---'}</div>
    </div>
  );
};

// ─── 工具：递归收集整棵树所有节点 id ─────────────────────────────────────────

const collectTreeAllIds = nodes => {
  const ids = [];
  nodes.forEach(node => {
    if (node.node_type === 'ib') {
      ids.push(...collectAllIds(node));
    } else {
      ids.push(node.id);
    }
  });
  return ids;
};

// ─── 主表头 ───────────────────────────────────────────────────────────────────

const TableHeader = ({ ibTree }) => {
  const dispatch = useDispatch();
  const selectedContactsIds = useSelector(state => state.crm.contacts.selectedContactsIds);

  const allIds = useMemo(() => collectTreeAllIds(ibTree?.results || []), [ibTree]);
  const isAllSelected = allIds.length > 0 && allIds.every(id => selectedContactsIds.includes(id));

  const handleHeaderCheckboxChange = () => {
    if (isAllSelected) {
      dispatch(crmSetSelectedContactsIds([]));
    } else {
      dispatch(crmSetSelectedContactsIds(Array.from(new Set([...selectedContactsIds, ...allIds]))));
    }
  };

  const hasResults = ibTree?.results && ibTree.results.length > 0;

  return (
    <div className="IBTreeTable__Header-Row">
      {hasResults && (
        <div className="IBTreeTable__Header-Cell IBTreeTable__Cell--checkbox">
          <div className="checkboxWrapper">
            <input type="checkbox" className="CheckBox" checked={isAllSelected} onChange={handleHeaderCheckboxChange} />
          </div>
        </div>
      )}
      <div className="IBTreeTable__Header-Cell">
        <FM id="justName" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <FM id="justLastSeen" />
      </div>
      <div className="IBTreeTable__Header-Cell">Manager Name</div>
      <div className="IBTreeTable__Header-Cell">
        <div className="IBTreeTable__Header-Top">
          <MoneyCell crmStyle heightMaxContent value={ibTree.total_in_sum} />
        </div>
        <FM id="justIn" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <div className="IBTreeTable__Header-Top">
          <MoneyCell crmStyle heightMaxContent value={ibTree.total_out_sum} />
        </div>
        <FM id="justOut" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <div className="IBTreeTable__Header-Top">
          <MoneyCell
            crmStyle
            heightMaxContent
            value={
              ibTree.total_in_sum && ibTree.total_out_sum
                ? {
                    amount: (+ibTree.total_in_sum.amount - +ibTree.total_out_sum.amount).toFixed(2),
                    currency: ibTree.total_in_sum.currency,
                  }
                : null
            }
          />
        </div>
        <FM id="justInAndOut" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <FM id="crmTradingAccount" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <FM id="justBalance" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <FM id="justTotalLots" />
      </div>
      <div className="IBTreeTable__Header-Cell">
        <FM id="justLastComment" />
      </div>
    </div>
  );
};

// ─── IBTreeTable 主组件 ───────────────────────────────────────────────────────

const IBTreeTable = ({ getContactsIBTree: fetchIBTree, ibTree, ibTreeIsLoaded, onTagSelect, location }) => {
  const dispatch = useDispatch();
  const lastQueryKey = useRef(null);

  const query = useMemo(() => {
    const urlQuery = queryString.parse(location.search);
    if (Object.keys(urlQuery).length > 0) {
      return urlQuery;
    }
    return queryString.parse(sessionStorage.getItem('fastFiltersQuery') || '');
  }, [location.search]);

  // 进入页面时清空选中状态
  useEffect(() => {
    dispatch(crmSetSelectedContactsIds([]));
  }, []);

  useEffect(() => {
    const defaultTradingDate = `2025-01-01_${new Date().toISOString().split('T')[0]}`;
    const finalQuery = {
      ...query,
      page: 1,
      page_size: 10,
      trading_date: query.trading_date || defaultTradingDate,
    };
    const queryKey = JSON.stringify(finalQuery);
    if (lastQueryKey.current === queryKey) return;
    lastQueryKey.current = queryKey;
    fetchIBTree(finalQuery);
  });

  const handleChangePage = (page, size) => {
    fetchIBTree({ ...query, page, page_size: size });
  };

  // 直接使用后端返回的过滤结果（后端已经根据所有筛选条件进行了过滤）
  const results = ibTree?.results || [];

  return (
    <div className="IBTreeTable">
      <PageContentPreloader ready={ibTreeIsLoaded} type="crmTable" noPadding>
        <div className="IBTreeTable__wrapper">
          <TableHeader ibTree={ibTree} />
          {!results.length ? (
            <NoItemsPanel
              icon={noPaymentsImage}
              containerMaxHeight
              captionSlug="crmThereAreNoItemsMatchingParameters"
            />
          ) : (
            <div className="IBTreeTable__Tbody">
              {results.map((node, idx) =>
                node.node_type === 'ib' ? (
                  <IBSummaryRow
                    key={`ib-${node.id}-${idx}`}
                    node={node}
                    depth={0}
                    onTagSelect={onTagSelect}
                    defaultOpen={!!query.search}
                  />
                ) : (
                  <UserRow key={`user-${node.id}-${idx}`} rowData={node} depth={0} onTagSelect={onTagSelect} />
                )
              )}
            </div>
          )}
        </div>
      </PageContentPreloader>
      <Pagination
        externalClass="IBTreeTable__pagination"
        dataCount={ibTree?.count || 0}
        handleChangePage={handleChangePage}
        sizes={[10, 50, 100]}
        searchId=""
        pending={!ibTreeIsLoaded}
      />
      <style jsx global>
        {staticStyles}
      </style>
    </div>
  );
};

IBTreeTable.propTypes = {
  getContactsIBTree: PropTypes.func.isRequired,
  ibTree: PropTypes.object.isRequired,
  ibTreeIsLoaded: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  onTagSelect: PropTypes.func,
};

IBTreeTable.defaultProps = {
  onTagSelect: () => {},
};

export default compose(
  withRouter,
  connect(
    state => ({
      ibTree: state.crm.contacts.ibTree,
      ibTreeIsLoaded: state.crm.contacts.ibTreeIsLoaded,
    }),
    {
      getContactsIBTree: search => getContactsIBTree.request(search),
    }
  )
)(IBTreeTable);
